export const CART_FRAGMENT = `
  fragment CartProps on Cart {
    id
    updatedAt
    attributes {
      key
      value
    }
    note
    buyerIdentity {
      email
    }
    checkoutUrl
    discountCodes {
      applicable
      code
    }
    cost {
      checkoutChargeAmount {
        amount
        currencyCode
      }
      subtotalAmount {
        amount
        currencyCode
      }
      totalAmount {
        amount
        currencyCode
      }
      subtotalAmountEstimated
      totalAmountEstimated
    }
    discountAllocations {
      ...on CartCodeDiscountAllocation {
        code
        discountedAmount {
          amount
          currencyCode
        }
      }
    }
    lines(first: 20) {
      nodes {
        id
        quantity
        attributes {
          key
          value
        }
        cost {
          amountPerQuantity {
            amount
            currencyCode
          }
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
        discountAllocations {
          ...on CartCodeDiscountAllocation {
            code
            discountedAmount {
              amount
              currencyCode
            }
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            id
            name
          }
        }
        merchandise {
          ...on ProductVariant {
            id
            sku
            title
            product {
              id
              handle
            }
            unitPrice {
              amount
              currencyCode
            }
            price {
              amount
              currencyCode
            }
          }
        }
      }
    }
  }
`;

/**
 * Query to fetch a cart by id
 */
export const CART = `
  query GetCart($id: ID!) {
    cart(id: $id) {
      ...CartProps
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * Query to create a new cart with the given lines and discount codes and buyer identity
 */
export const CART_CREATE = `
  mutation CreateCart($lines: [CartLineInput!], $discountCodes: [String!], $buyerIdentity: CartBuyerIdentityInput) {
    cartCreate(input: { lines: $lines, discountCodes: $discountCodes, buyerIdentity: $buyerIdentity }) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * Query to add lines to a cart
 */
export const CART_ADD_LINES = `
  mutation CartAddLines($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * Query to update lines in a cart
 */
export const CART_UPDATE_LINES = `
  mutation CartUpdateLines($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

/**
 * Query to remove lines in a cart
 */
export const CART_REMOVE_LINES = `
  mutation CartRemoveLines($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_NOTE_UPDATE = `
  mutation CartUpdateNote($cartId: ID!, $note: String!) {
    cartNoteUpdate(cartId: $cartId, note: $note) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_ATTRIBUTES_UPDATE = `
  mutation CartUpdateAttributes($cartId: ID!, $attributes: [AttributeInput!]!) {
    cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_BUYER_IDENTITY_UPDATE = `
  mutation CartUpdateBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;

export const CART_DISCOUNT_CODES_UPDATE = `
  mutation CartUpdateDiscountCodes($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        ...CartProps
      }
      userErrors {
        code
        message
      }
    }
  }
  ${CART_FRAGMENT}
`;
